import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  AlertTitle,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

dayjs.locale('it');

const COUNT_LABELS = {
  courses: 'Corsi',
  enrollments: 'Iscrizioni (con registro presenze)',
  payments: 'Pagamenti',
  memberships: 'Tesseramenti',
  expenses: 'Spese',
  transfers: 'Giroconti docenti',
  teacherOtherCompensations: 'Rimborsi docenti',
  reservations: 'Prenotazioni'
};

const formatAmount = (value) => `${Number(value || 0).toFixed(2)}€`;

const formatDate = (value) => (value ? dayjs(value).format('DD MMM YYYY') : '-');

const ModalArchivePreview = ({ open, loading, preview, label, onArchive, onCancel }) => {
  const counts = preview?.counts || {};
  const totalDocuments = Object.values(counts).reduce((sum, count) => sum + count, 0);
  const canArchive = !loading && preview && !preview.isEmpty && !preview.blockedBy;

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onCancel();
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Anteprima archiviazione</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && preview && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Archivio <strong>{label}</strong> — periodo dal {formatDate(preview.periodFrom)} al{' '}
              {formatDate(preview.periodTo)}, data di taglio {formatDate(preview.cutoffDate)}.
            </Typography>

            {preview.blockedBy && (
              <Alert severity="error" sx={{ my: 2 }}>
                <AlertTitle>Archiviazione precedente incompleta</AlertTitle>
                L'archivio "{preview.blockedBy.label}" e' rimasto a meta'. Annullalo dalla tabella degli archivi prima di
                avviarne uno nuovo.
              </Alert>
            )}

            {preview.isEmpty && (
              <Alert severity="info" sx={{ my: 2 }}>
                Non c'e' nulla da archiviare con questa data di taglio.
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Documenti che verranno archiviati ({totalDocuments})
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Collezione</TableCell>
                    <TableCell align="right">Documenti</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(COUNT_LABELS).map(([key, label]) => (
                    <TableRow key={key}>
                      <TableCell>{label}</TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={counts[key] || 0}
                          color={counts[key] ? 'primary' : 'default'}
                          variant={counts[key] ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Bilancio dell'anno archiviato
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label={`Entrate ${formatAmount(preview.totals?.income)}`} color="success" variant="outlined" />
              <Chip label={`Uscite ${formatAmount(preview.totals?.expenses)}`} color="error" variant="outlined" />
              <Chip
                label={`Saldo ${formatAmount(preview.totals?.net)}`}
                color={Number(preview.totals?.net) >= 0 ? 'success' : 'error'}
              />
            </Box>

            {preview.excluded?.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Cosa resta fuori dall'archivio
                </Typography>
                {preview.excluded.map((group) => (
                  <Alert key={`${group.entity}-${group.reason}`} severity="warning" sx={{ mb: 1 }}>
                    <AlertTitle>
                      {group.entity} — {group.count}
                    </AlertTitle>
                    <Typography variant="body2" sx={{ mb: group.items?.length ? 1 : 0 }}>
                      {group.reason}
                    </Typography>
                    {group.items?.map((item) => (
                      <Typography key={item.id} variant="caption" component="div" sx={{ pl: 1 }}>
                        • {item.label}
                      </Typography>
                    ))}
                    {group.count > (group.items?.length || 0) && (
                      <Typography variant="caption" component="div" sx={{ pl: 1, fontStyle: 'italic' }}>
                        … e altri {group.count - group.items.length}
                      </Typography>
                    )}
                  </Alert>
                ))}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Annulla
        </Button>
        <Button onClick={onArchive} color="error" variant="contained" disabled={!canArchive}>
          Procedi con l'archiviazione
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalArchivePreview;
